import React, { useEffect } from 'react';

/**
 * FloatingAlert
 * Props :
 * - open : bool (affiche ou non l'alerte)
 * - onClose : fonction appelée au clic sur X
 * - children : contenu JSX
 * - type : 'info' | 'warning' | 'error' | 'success' (optionnel, défaut 'info')
 */
const typeConfig = {
  info: {
    border: 'border-blue-300',
    text: 'text-blue-700',
    bg: 'bg-blue-50',
    icon: (
      <svg className="w-6 h-6 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    )
  },
  warning: {
    border: 'border-yellow-300',
    text: 'text-yellow-700',
    bg: 'bg-yellow-50',
    icon: (
      <svg className="w-6 h-6 text-yellow-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
      </svg>
    )
  },
  error: {
    border: 'border-red-300',
    text: 'text-red-700',
    bg: 'bg-red-50',
    icon: (
      <svg className="w-6 h-6 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    )
  },
  success: {
    border: 'border-green-300',
    text: 'text-green-700',
    bg: 'bg-green-50',
    icon: (
      <svg className="w-6 h-6 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    )
  }
};

export default function FloatingAlert({ open, onClose, children, type = 'info' }) {
  useEffect(() => {
    if (open) {
      const timer = setTimeout(() => {
        onClose();
      }, 2000);

      return () => clearTimeout(timer);
    }
  }, [open, onClose]);

  if (!open) return null;

  const config = typeConfig[type] || typeConfig.info;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center pointer-events-none">
      <div 
        className={`
          ${config.bg} ${config.border} border rounded-lg shadow-lg p-4 
          max-w-sm w-full flex items-start gap-3 relative pointer-events-auto
          transform transition-all duration-300 ease-in-out
          animate-fade-in-up
        `}
      >
        <div className="flex-shrink-0 mt-0.5">
          {config.icon}
        </div>
        <div className={`${config.text} flex-1 text-sm font-medium`}>
          {children}
        </div>
        <button
          onClick={onClose}
          className="text-gray-400 hover:text-gray-600 focus:outline-none transition-colors duration-200"
          aria-label="Fermer l'alerte"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
    </div>
  );
} 